import { apiGet, apiPost, apiPatch } from './apiClient.js';

/**
 * Admin Properties Service
 * For querying properties
 */

/**
 * Get all properties with filters and pagination
 * @param {object} params
 * @param {number} params.limit - Max results
 * @param {string} params.cursor - Cursor for pagination
 * @param {string} params.q - Search query
 * @param {string} params.status - Filter by status (active/inactive)
 * @param {AbortSignal} [params.signal] - Optional AbortSignal for request cancellation
 * @returns {Promise<object>}
 */
export async function getProperties(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.cursor) queryParams.append('cursor', params.cursor);
  if (params.q) queryParams.append('q', params.q);
  if (params.status) queryParams.append('status', params.status);

  const queryString = queryParams.toString();
  const { signal, ...restParams } = params;
  return apiGet(`/admin/properties${queryString ? `?${queryString}` : ''}`, {
    ...(signal && { signal }),
  });
}

/**
 * Get property by ID
 * @param {string} id - Property ID
 * @returns {Promise<object>}
 */
export async function getProperty(id) {
  return apiGet(`/admin/properties/${id}`);
}

/**
 * Update property fields (whitelist: code, name, status)
 * @param {string} id - Property ID
 * @param {object} updates - Updates object (code, name, status)
 * @param {string} reason - Reason for update
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>}
 */
export async function updateProperty(id, updates, reason, intentAuditEventId) {
  if (!reason || !reason.trim()) {
    throw new Error('Reason is required');
  }
  if (!intentAuditEventId) {
    throw new Error('Intent audit event ID is required');
  }

  return apiPatch(`/admin/properties/${id}`, {
    ...updates,
    reason: reason.trim(),
    intentAuditEventId,
  });
}

/**
 * Assign owner to property
 * @param {string} id - Property ID
 * @param {string|{ownerId?: string, owner_user_id?: string, ownerUserId?: string}} ownerSpec - Legacy ownerId string or dual-key object
 * @param {string} reason - Reason for assignment
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>}
 */
export async function assignOwner(id, ownerSpec, reason, intentAuditEventId) {
  const ownerId =
    typeof ownerSpec === 'string'
      ? ownerSpec
      : ownerSpec?.ownerId ?? ownerSpec?.owner_id ?? null;
  const ownerUserId =
    typeof ownerSpec === 'string'
      ? null
      : ownerSpec?.ownerUserId ?? ownerSpec?.owner_user_id ?? null;
  if ((!ownerId || !String(ownerId).trim()) && (!ownerUserId || !String(ownerUserId).trim())) {
    throw new Error('Owner ID or owner_user_id is required');
  }
  if (!reason || !reason.trim()) {
    throw new Error('Reason is required');
  }
  if (!intentAuditEventId) {
    throw new Error('Intent audit event ID is required');
  }

  return apiPost(`/admin/properties/${id}/assign-owner`, {
    ...(ownerId && String(ownerId).trim() ? { ownerId: String(ownerId).trim() } : {}),
    ...(ownerUserId && String(ownerUserId).trim() ? { owner_user_id: String(ownerUserId).trim() } : {}),
    reason: reason.trim(),
    intentAuditEventId,
  });
}

/**
 * Unassign owner from property
 * @param {string} id - Property ID
 * @param {string} reason - Reason for unassignment
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>}
 */
export async function unassignOwner(id, reason, intentAuditEventId) {
  if (!reason || !reason.trim()) {
    throw new Error('Reason is required');
  }
  if (!intentAuditEventId) {
    throw new Error('Intent audit event ID is required');
  }

  return apiPost(`/admin/properties/${id}/unassign-owner`, {
    reason: reason.trim(),
    intentAuditEventId,
  });
}

/**
 * Bulk set property status
 * @param {Array<string>} propertyIds - Array of property IDs
 * @param {string} status - Status (active/inactive)
 * @param {string} reason - Reason for update
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>}
 */
export async function bulkSetPropertyStatus(propertyIds, status, reason, intentAuditEventId) {
  return apiPost('/admin/properties/bulk/status', {
    propertyIds,
    status,
    reason,
    intentAuditEventId,
  });
}

/**
 * Bulk assign owner to properties
 * @param {Array<string>} propertyIds - Array of property IDs
 * @param {string|{ownerId?: string, owner_user_id?: string, ownerUserId?: string}} ownerSpec - Legacy ownerId string or dual-key object
 * @param {string} reason - Reason for assignment
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>}
 */
export async function bulkAssignOwner(propertyIds, ownerSpec, reason, intentAuditEventId) {
  const ownerId =
    typeof ownerSpec === 'string'
      ? ownerSpec
      : ownerSpec?.ownerId ?? ownerSpec?.owner_id ?? null;
  const ownerUserId =
    typeof ownerSpec === 'string'
      ? null
      : ownerSpec?.ownerUserId ?? ownerSpec?.owner_user_id ?? null;
  return apiPost('/admin/properties/bulk/assign-owner', {
    propertyIds,
    ...(ownerId ? { ownerId } : {}),
    ...(ownerUserId ? { owner_user_id: ownerUserId } : {}),
    reason,
    intentAuditEventId,
  });
}

/**
 * Bulk unassign owner from properties
 * @param {Array<string>} propertyIds - Array of property IDs
 * @param {string} reason - Reason for unassignment
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>}
 */
export async function bulkUnassignOwner(propertyIds, reason, intentAuditEventId) {
  return apiPost('/admin/properties/bulk/unassign-owner', {
    propertyIds,
    reason,
    intentAuditEventId,
  });
}

/**
 * Export properties CSV (selected)
 * @param {Array<string>} ids - Array of property IDs
 * @returns {Promise<Blob>}
 */
export async function exportPropertiesCsvSelected(ids) {
  const queryParams = new URLSearchParams();
  queryParams.append('scope', 'selected');
  queryParams.append('ids', ids.join(','));
  
  const response = await fetch(`/api/v1/admin/properties/export?${queryParams.toString()}`, {
    headers: {
      'X-DEV-IS-SUPERADMIN': 'true',
      'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }
  
  return response.blob();
}

/**
 * Export properties CSV (current page)
 * @param {object} queryParams - Query parameters (q, status, cursor, limit)
 * @returns {Promise<Blob>}
 */
export async function exportPropertiesCsvPage(queryParams = {}) {
  const params = new URLSearchParams();
  params.append('scope', 'page');
  
  if (queryParams.q) params.append('q', queryParams.q);
  if (queryParams.status) params.append('status', queryParams.status);
  if (queryParams.cursor) params.append('cursor', queryParams.cursor);
  if (queryParams.limit) params.append('limit', queryParams.limit);
  
  const response = await fetch(`/api/v1/admin/properties/export?${params.toString()}`, {
    headers: {
      'X-DEV-IS-SUPERADMIN': 'true',
      'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }
  
  return response.blob();
}

